import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, MapPin, FileText, DollarSign, User, Building, Phone, Mail, AlertCircle, Loader2 } from 'lucide-react';
import api from '../../lib/api';

const QuoteViewPage = () => {
  const { clientToken } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    fetchQuote();
  }, [clientToken]);

  const fetchQuote = async () => {
    try {
      setLoading(true);
      const response = await api.getQuoteForClient(clientToken);
      const data = response.data;
      
      // Combine quote and server data into a flat structure
      const combinedQuote = {
        ...data.quote,
        server_name: data.server?.display_name,
        server_company: data.server?.company_name,
        server_photo: data.server?.photo_url,
        trust_score: data.server?.trust_score,
        // Add fields from direct quote
        client_name: data.quote?.client_name,
        client_email: data.quote?.client_email,
        client_phone: data.quote?.client_phone,
        quoted_price: data.quote?.quoted_price || data.quote?.response?.quoted_price,
        response_message: data.quote?.response_message || data.quote?.response?.message
      };
      
      setQuote(combinedQuote);
      if (combinedQuote.status === 'accepted') {
        setAccepted(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Quote not found');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    try {
      setAccepting(true);
      await api.acceptQuoteByToken(clientToken);
      setAccepted(true);
      setQuote(prev => ({ ...prev, status: 'accepted' }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to accept quote');
    } finally {
      setAccepting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatAddress = (addr) => {
    if (!addr) return 'N/A';
    const parts = [addr.street, addr.city, addr.state, addr.zip].filter(Boolean);
    return parts.join(', ');
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'responded':
        return { 
          label: 'QUOTE READY', 
          color: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
          icon: CheckCircle
        };
      case 'accepted':
        return { 
          label: 'ACCEPTED', 
          color: 'bg-green-500/20 text-green-400 border-green-500/30',
          icon: CheckCircle
        };
      case 'pending':
        return { 
          label: 'PENDING', 
          color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
          icon: Clock
        };
      case 'declined':
        return { 
          label: 'DECLINED', 
          color: 'bg-red-500/20 text-red-400 border-red-500/30',
          icon: XCircle
        };
      default:
        return { 
          label: status?.toUpperCase() || 'UNKNOWN', 
          color: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
          icon: AlertCircle
        };
    }
  };

  const getUrgencyConfig = (urgency) => {
    switch (urgency) {
      case 'same_day':
        return { label: 'SAME DAY', color: 'bg-red-500/20 text-red-400' };
      case 'rush':
        return { label: 'RUSH (24H)', color: 'bg-amber-500/20 text-amber-400' };
      default:
        return { label: 'STANDARD', color: 'bg-green-500/20 text-green-400' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-teal-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading quote...</p>
        </div>
      </div>
    );
  }

  if (error && !quote) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Quote Not Found</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-teal-600 hover:bg-teal-500 text-white font-semibold rounded-lg transition-colors"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(quote?.status);
  const urgencyConfig = getUrgencyConfig(quote?.urgency);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-xl sm:text-2xl font-bold text-teal-500">Servio</h1>
              <span className="text-slate-600">|</span>
              <span className="text-slate-400 text-sm sm:text-base">Quote Details</span>
            </div>
            <span className={`px-3 py-1.5 rounded-full text-[10px] font-bold tracking-wider border ${statusConfig.color}`}>
              {statusConfig.label}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Success Message */}
        {accepted && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 sm:p-6 mb-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h2 className="text-lg sm:text-xl font-bold text-green-400 mb-2">Quote Accepted!</h2>
            <p className="text-slate-400 text-sm sm:text-base">
              {quote?.server_name} has been notified and will proceed with your service.
            </p>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Quote Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Server Info Card */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-slate-800">
                <p className="text-[10px] font-bold text-slate-500 tracking-widest mb-3">PROCESS SERVER</p>
                <div className="flex items-start gap-4">
                  {quote?.server_photo ? (
                    <img 
                      src={quote.server_photo} 
                      alt={quote.server_name}
                      className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-slate-800 flex items-center justify-center">
                      <User className="w-7 h-7 text-slate-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg sm:text-xl font-bold text-white truncate">{quote?.server_name}</h3>
                    {quote?.server_company && (
                      <p className="text-slate-400 text-sm flex items-center gap-1.5 mt-1">
                        <Building className="w-4 h-4" />
                        <span className="truncate">{quote.server_company}</span>
                      </p>
                    )}
                    {quote?.trust_score && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-amber-400 text-sm">★</span>
                        <span className="text-white font-semibold">{quote.trust_score}</span>
                        <span className="text-[10px] font-bold text-slate-500 tracking-wider">SERVIO SCORE</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Price Section */}
              {quote?.quoted_price && (
                <div className="p-4 sm:p-6 bg-gradient-to-r from-teal-500/10 to-teal-600/5 border-b border-slate-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-bold text-slate-500 tracking-widest mb-1">QUOTED PRICE</p>
                      <p className="text-3xl sm:text-4xl font-bold text-teal-400">
                        ${quote.quoted_price.toFixed(2)}
                      </p>
                    </div>
                    <DollarSign className="w-10 h-10 text-teal-500/30" />
                  </div>
                </div>
              )}

              {/* Server Message */}
              {quote?.response_message && (
                <div className="p-4 sm:p-6">
                  <p className="text-[10px] font-bold text-slate-500 tracking-widest mb-2">MESSAGE FROM SERVER</p>
                  <p className="text-slate-300 text-sm sm:text-base leading-relaxed italic">
                    "{quote.response_message}"
                  </p>
                </div>
              )}
            </div>

            {/* Service Details Card */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-6">
              <p className="text-[10px] font-bold text-slate-500 tracking-widest mb-4">SERVICE DETAILS</p>
              
              <div className="space-y-4">
                {/* Address */}
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold text-slate-500 tracking-wider mb-1">SERVICE ADDRESS</p>
                    <p className="text-white text-sm sm:text-base">{formatAddress(quote?.service_address)}</p>
                  </div>
                </div>

                {/* Document Type */}
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold text-slate-500 tracking-wider mb-1">DOCUMENT TYPE</p>
                    <p className="text-white text-sm sm:text-base">{quote?.document_type || 'Not specified'}</p>
                  </div>
                </div>

                {/* Urgency & Serves */}
                <div className="flex flex-wrap gap-3">
                  <span className={`px-3 py-1.5 rounded-full text-[10px] font-bold tracking-wider ${urgencyConfig.color}`}>
                    {urgencyConfig.label}
                  </span>
                  <span className="px-3 py-1.5 rounded-full text-[10px] font-bold tracking-wider bg-slate-800 text-slate-300">
                    {quote?.num_serves || 1} SERVE{(quote?.num_serves || 1) > 1 ? 'S' : ''}
                  </span>
                </div>

                {/* Additional Notes */}
                {quote?.additional_details && (
                  <div className="pt-4 border-t border-slate-800">
                    <p className="text-[10px] font-bold text-slate-500 tracking-wider mb-2">ADDITIONAL NOTES</p>
                    <p className="text-slate-400 text-sm bg-slate-800/50 rounded-lg p-3 border-l-2 border-teal-500">
                      {quote.additional_details}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Actions */}
          <div className="space-y-6">
            {/* Accept Quote Card */}
            {quote?.status === 'responded' && !accepted && (
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-6">
                <h3 className="font-bold text-white mb-3">Ready to proceed?</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Accept this quote to confirm {quote?.server_name} for your service.
                </p>
                <button
                  onClick={handleAccept}
                  disabled={accepting}
                  data-testid="accept-quote-btn"
                  className="w-full py-3 sm:py-4 bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400 text-white font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {accepting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-sm tracking-wide">ACCEPTING...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span className="text-sm tracking-wide">ACCEPT QUOTE</span>
                    </>
                  )}
                </button>
                {error && (
                  <p className="text-red-400 text-sm mt-3 text-center">{error}</p>
                )}
              </div>
            )}

            {/* Already Accepted */}
            {(quote?.status === 'accepted' || accepted) && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 sm:p-6 text-center">
                <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
                <h3 className="text-[11px] font-bold tracking-wider text-green-400 mb-2">QUOTE ACCEPTED</h3>
                <p className="text-slate-400 text-sm">
                  The server has been notified and will contact you to proceed.
                </p>
              </div>
            )}

            {/* Pending Response */}
            {quote?.status === 'pending' && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 sm:p-6 text-center">
                <Clock className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                <h3 className="text-[11px] font-bold tracking-wider text-amber-400 mb-2">AWAITING RESPONSE</h3>
                <p className="text-slate-400 text-sm">
                  The server hasn't responded yet. You'll receive an email when they do.
                </p>
              </div>
            )}

            {/* Declined */}
            {quote?.status === 'declined' && (
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 sm:p-6 text-center">
                <XCircle className="w-10 h-10 text-slate-500 mx-auto mb-3" />
                <h3 className="text-[11px] font-bold tracking-wider text-slate-400 mb-2">QUOTE DECLINED</h3>
                <p className="text-slate-500 text-sm">
                  The server was unable to take this job.
                </p>
                <button
                  onClick={() => navigate('/search')}
                  className="mt-4 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  Find Another Server
                </button>
              </div>
            )}

            {/* Timeline Card */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-6">
              <p className="text-[10px] font-bold text-slate-500 tracking-widest mb-4">TIMELINE</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-teal-500"></div>
                  <span className="text-slate-400">Requested</span>
                  <span className="text-slate-500 ml-auto text-xs">{formatDate(quote?.created_at)}</span>
                </div>
                {quote?.responded_at && (
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-teal-500"></div>
                    <span className="text-slate-400">Quote Received</span>
                    <span className="text-slate-500 ml-auto text-xs">{formatDate(quote.responded_at)}</span>
                  </div>
                )}
                {quote?.accepted_at && (
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-slate-400">Accepted</span>
                    <span className="text-slate-500 ml-auto text-xs">{formatDate(quote.accepted_at)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-6">
              <p className="text-[10px] font-bold text-slate-500 tracking-widest mb-4">YOUR REQUEST INFO</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-300 text-sm">{quote?.client_name}</span>
                </div>
                {quote?.client_email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-300 text-sm truncate">{quote.client_email}</span>
                  </div>
                )}
                {quote?.client_phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-300 text-sm">{quote.client_phone}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteViewPage;
